import { atom, useAtom } from 'jotai';
import { atomWithQuery, atomWithMutation } from 'jotai-tanstack-query';
import { invalidateQueries } from '@/lib/query-client';
import { pulseService } from '@/services/pulse.service';
import { buScopeKey, buScopeParams } from '@/lib/business-unit-scope';

// null = never fetched (show empty state); object = fetch triggered (show table)
const pulseParamsAtom = atom<{ startDate?: string; endDate?: string } | null>(null);

const pulseSubcategoryCountsAtom = atomWithQuery((get) => {
  const params = get(pulseParamsAtom);
  const scope = buScopeParams(get);
  return {
    queryKey: ['pulse', 'subcategory-counts', params, buScopeKey(get)],
    queryFn: () => pulseService.getSubcategoryCounts({ ...(params ?? {}), ...scope }),
    enabled: params !== null,
    staleTime: Infinity
  };
});

const generateSOPMutationAtom = atomWithMutation(() => ({
  mutationKey: ['pulse', 'generate-sop'],
  mutationFn: ({
    sopCatId,
    startDate,
    endDate,
    integrations
  }: {
    sopCatId: string;
    startDate?: string;
    endDate?: string;
    integrations: string[];
  }) => pulseService.generateSOP(sopCatId, startDate, endDate, integrations)
}));

export function usePulseSubcategoryCounts() {
  const [, setParams] = useAtom(pulseParamsAtom);
  const [params] = useAtom(pulseParamsAtom);
  const [query] = useAtom(pulseSubcategoryCountsAtom);

  const fetchTickets = (dateParams?: { startDate?: string; endDate?: string }) => {
    setParams(dateParams ?? {});
  };

  const refetch = () => {
    invalidateQueries(['pulse', 'subcategory-counts']);
  };

  // fetchedRange: the dates the counts on screen are for — drafting must use the same ones.
  return { ...query, fetchTickets, refetch, hasFetched: params !== null, fetchedRange: params };
}

export function useGenerateSOP() {
  const [mutation] = useAtom(generateSOPMutationAtom);
  return mutation;
}
